import React, { useMemo } from 'react';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download.svg';
import { GetScanResponse } from '../../api/scans';
import { GetMemberResponse } from '../../types/apiGeneratedTypes';
import { buildConsolidatedReportModel } from './consolidatedReportModel';
import { getStatusDisplayForItem } from './fbsFindingStatuses';
import {
  FbsReportData,
  getConsolidatedStatusRowClass,
  getFindingFilters,
  resolveUsesFbsFindingStatusV2
} from './fullBodyScanReportUtils';

interface Props {
  scanData: GetScanResponse;
  reportData: FbsReportData;
  member?: GetMemberResponse | null;
  onDownload?: () => void;
  isDownloading?: boolean;
  selectedStatuses: string[];
  onSelectedStatusesChange: (statuses: string[]) => void;
}

const FullBodyScanConsolidated: React.FC<Props> = ({
  scanData,
  reportData,
  member,
  onDownload,
  isDownloading,
  selectedStatuses,
  onSelectedStatusesChange
}) => {
  const bookingUsesV2 = useMemo(
    () =>
      resolveUsesFbsFindingStatusV2(
        scanData as Record<string, unknown>,
        reportData
      ),
    [scanData, reportData]
  );
  const findingFilters = useMemo(() => getFindingFilters(bookingUsesV2), [bookingUsesV2]);

  const model = useMemo(
    () =>
      buildConsolidatedReportModel({
        scanData,
        reportData,
        member,
        selectedStatuses
      }),
    [scanData, reportData, member, selectedStatuses]
  );

  const counts = model.findingCounts;

  const toggleStatus = (filterId: string) => {
    onSelectedStatusesChange(
      selectedStatuses.includes(filterId)
        ? selectedStatuses.filter((s) => s !== filterId)
        : [...selectedStatuses, filterId]
    );
  };

  return (
    <div className="full-body-scan-consolidated">
      <div className="consolidated-title-row">
        <h3 className="consolidated-title">Full Body Scan — Consolidated Report</h3>
        {onDownload ? (
          <button
            type="button"
            className="consolidated-download-btn"
            onClick={onDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              'Preparing…'
            ) : (
              <>
                <DownloadIcon className="consolidated-download-icon" aria-hidden />
                Download report
              </>
            )}
          </button>
        ) : null}
      </div>

      <div className="consolidated-gray-card">
        <h4 className="consolidated-card-title">Patient booking information</h4>
        <div className="consolidated-booking-grid">
          <div className="consolidated-booking-col">
            <div className="consolidated-kv">
              <span className="consolidated-k">Patient name</span>
              <span className="consolidated-v">{model.patientName}</span>
            </div>
            <div className="consolidated-kv">
              <span className="consolidated-k">Scan date &amp; time</span>
              <span className="consolidated-v">{model.scanDateTimeLine}</span>
            </div>
            <div className="consolidated-kv">
              <span className="consolidated-k">Date of birth</span>
              <span className="consolidated-v">{model.dateOfBirth}</span>
            </div>
            <div className="consolidated-kv">
              <span className="consolidated-k">Booking ID</span>
              <span className="consolidated-v">{model.bookingId}</span>
            </div>
          </div>
          <div className="consolidated-booking-col consolidated-booking-col--right">
            <div className="consolidated-kv">
              <span className="consolidated-k">Gender</span>
              <span className="consolidated-v">{model.gender}</span>
            </div>
            <div className="consolidated-kv">
              <span className="consolidated-k">Radiologist</span>
              <span className="consolidated-v">{model.radiologist}</span>
            </div>
            <div className="consolidated-kv">
              <span className="consolidated-k">GP</span>
              <span className="consolidated-v">{model.gp}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="consolidated-gray-card">
        <h4 className="consolidated-card-title">Conclusions and Recommendations</h4>
        {model.followUpLabel ? (
          <h5 className="consolidated-subheading">{model.followUpLabel}</h5>
        ) : null}
        {model.conclusionBody ? (
          <p className="consolidated-body-text">{model.conclusionBody}</p>
        ) : (
          <p className="consolidated-muted">{model.conclusionEmptyMessage}</p>
        )}
      </div>

      <div className="consolidated-gray-card">
        <h4 className="consolidated-card-title">Radiologist notes</h4>
        <div className="consolidated-notes-stack">
          <div>
            <div className="consolidated-field-label">Technique</div>
            <p className="consolidated-body-text">{model.technique}</p>
          </div>
          <div>
            <div className="consolidated-field-label">Scan Technical Satisfactory</div>
            <p className="consolidated-body-text">{model.scanTechnical}</p>
          </div>
          <div>
            <div className="consolidated-field-label">Scan Adequate Coverage</div>
            <p className="consolidated-body-text">{model.scanCoverage}</p>
          </div>
          <div>
            <div className="consolidated-field-label">Additional comments</div>
            <p className="consolidated-body-text">{model.additionalComments}</p>
          </div>
        </div>
      </div>

      <div className="consolidated-filter-bar">
        <div className="consolidated-filter-summary" title={model.filterSummary}>
          <span className="consolidated-filter-label">Filter</span>
          <span className="consolidated-filter-value">{model.filterSummary}</span>
        </div>
        <div className="consolidated-filter-chips">
          {findingFilters.map((filter) => {
            const selected = selectedStatuses.includes(filter.id);
            return (
              <button
                key={filter.id}
                type="button"
                className={`consolidated-chip ${selected ? 'selected' : ''} ${filter.key}`}
                onClick={() => toggleStatus(filter.id)}
              >
                <span className="consolidated-chip-count">{counts[filter.id] || 0}</span>
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="consolidated-systems">
        {model.filteredSystems.map((system) => (
          <div key={system.key} className="consolidated-system-shell">
            <div className="consolidated-system-header">
              <div className="consolidated-system-name">{system.name}</div>
              <div className="consolidated-system-meta">
                <div>
                  <div className="consolidated-field-label">System status</div>
                  <div
                    className={`consolidated-status-tag ${getConsolidatedStatusRowClass(system.status, bookingUsesV2)}`}
                  >
                    {system.status}
                  </div>
                </div>
                <div className="consolidated-system-notes-box">
                  <div className="consolidated-field-label">System notes</div>
                  <p className="consolidated-system-notes-text">
                    {system.systemNote?.trim() || 'No notes available for this system.'}
                  </p>
                </div>
              </div>
            </div>

            {Object.entries(system.organs).map(([organKey, organData]) => {
              if (!organData.items?.length) return null;
              return (
                <div key={organKey} className="consolidated-organ-block">
                  <h5 className="consolidated-organ-title">{organData.name || organKey}</h5>
                  <div className="consolidated-outer-card">
                    <div className="consolidated-row-head">
                      <div>Conditions</div>
                      <div>Status</div>
                    </div>
                    {organData.items.map((item, index) => {
                      const statusDisplay = getStatusDisplayForItem(item, bookingUsesV2);
                      const rowStatus = statusDisplay.label;
                      return (
                        <div
                          key={item.id || `${organKey}-${index}`}
                          className="consolidated-inner-row"
                        >
                          <div className="consolidated-condition-col">
                            {item.condition || item.description || '-'}
                            {item.description &&
                            item.condition &&
                            item.description !== item.condition ? (
                              <span
                                className="consolidated-desc-hint"
                                title={item.description}
                                aria-label={`Description: ${item.description}`}
                              >
                                ℹ
                              </span>
                            ) : null}
                          </div>
                          <div className="consolidated-status-col">
                            <div
                              className={`consolidated-status-tag ${getConsolidatedStatusRowClass(rowStatus, bookingUsesV2)}`}
                            >
                              {rowStatus}
                            </div>
                            {item.detailedNote?.trim() ? (
                              <p className="consolidated-item-note">{item.detailedNote}</p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {model.filteredSystems.length === 0 && (
          <div className="consolidated-system-shell consolidated-empty-filters">
            <p>No findings match the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FullBodyScanConsolidated;
